<template>
  <v-page>
    <div class="my-content">
      <v-link v-if="isAuthenticated" to="/me/account">
        <toolbar :back="false" class="mb-3" :logo="false">
          <template v-slot:left>
            <div class="my-content__avatar font-weight-bold">
              <subscriber-avatar
                :subscriber="currentUser"
                :is-loading="isLoading"
              />
            </div>
            <div class="my-content__subscriber-details d-flex flex-column">
              <span class="v-toolbar__text ml-0">
                {{
                  isPresent(currentUser.name)
                    ? currentUser.name
                    : currentUser.email.split("@")[0]
                }}
              </span>
              <span class="text-small text-grey-70">
                View Account Details
              </span>
            </div>
          </template>
          <template v-slot:right>
            <v-button ghost class="v-toolbar__button">
              <v-icon icon="arrow_forward" />
            </v-button>
          </template>
        </toolbar>
      </v-link>

      <list
        v-if="
          canPrompt || showMyChats || !hasScheduleLink || hasFormSubmissions
        "
        boxed
        class="mb-4"
      >
        <add-to-homescreen is-list-item context="my_content" />

        <list-item
          v-if="showMyChats"
          chevron
          @click="() => $router.push('/me/messages')"
        >
          <template slot="left">
            <v-icon icon="forum" />
          </template>
          <unread-marker-dot />
          <div class="v-list-item__title --centered ml-2">
            Messages
          </div>
        </list-item>
        <list-item
          v-if="!hasScheduleLink"
          chevron
          @click="() => $router.push('/me/schedule')"
        >
          <template slot="left">
            <v-icon icon="calendar_today" />
          </template>
          <div class="v-list-item__title --centered ml-2">My Schedule</div>
        </list-item>

        <list-item
          v-if="hasFormSubmissions"
          chevron
          @click="() => $router.push('/me/submissions')"
        >
          <template slot="left">
            <v-icon icon="description" />
          </template>
          <div class="v-list-item__title --centered ml-2">My Submissions</div>
        </list-item>
      </list>

      <tabs
        class="my-content__tabs"
        :tabs="['My Content', 'My Activity', 'My Saved']"
      >
        <template v-slot:mycontent>
          <template v-if="hasMyContent">
            <collection
              v-if="hasPurchases"
              class="pb-1 mb-1"
              card-display="horizontal"
              :items="collectionPurchasedProducts"
              :show-price="false"
              modifiers="no-image"
              unlock
            >
              <h3 class="px-2 m-0">My Purchases</h3>
            </collection>

            <collection
              v-if="hasPacks"
              class="pb-1 mb-1"
              card-display="horizontal"
              :column-width="collectionLayout(packs)"
              :items="packs"
              :show-price="false"
              unlock
            >
              <h3 class="px-2 m-0">My Packs</h3>
            </collection>

            <collection
              v-if="hasSharedItems"
              class="pb-1 mb-1"
              card-display="horizontal"
              :items="sharedItems"
              :show-price="false"
              :space-bottom-ratio="250"
              unlock
            >
              <h3 class="px-2 m-0">Shared With Me</h3>
            </collection>

            <collection
              v-if="hasUpcomingPages"
              class="pb-1 mb-1"
              card-display="horizontal"
              :items="upcomingPages"
              modifiers="date-card"
              :show-price="false"
              :space-bottom-ratio="250"
              unlock
            >
              <h3 class="px-2 m-0">Upcoming</h3>
            </collection>

            <collection
              v-if="hasAllPrograms"
              class="pb-1 mb-1"
              card-display="horizontal"
              :items="allPrograms"
              :show-price="false"
              :space-bottom-ratio="250"
              unlock
            >
              <h3 class="px-2 m-0">My Programs</h3>
            </collection>

            <template v-for="contentType in Object.keys(progressByContentType)">
              <template v-if="contentType !== 'program'">
                <collection
                  :key="contentType"
                  class="pb-1 mb-1"
                  card-display="horizontal"
                  :items="progressByContentType[contentType]"
                  :show-price="false"
                  :space-bottom-ratio="250"
                  unlock
                >
                  <h3 class="px-2 m-0">
                    My {{ pluralize(formatSlug(contentType)) }}
                  </h3>
                </collection>
              </template>
            </template>

            <collection
              v-if="hasEvents"
              class="pb-1 mb-1"
              card-display="horizontal"
              :items="events"
              modifiers="date-card"
              :show-price="false"
              :space-bottom-ratio="250"
              unlock
            >
              <h3 class="px-2 m-0">Upcoming Events</h3>
            </collection>
          </template>

          <template v-else>
            <div class="text-center mt-2 p-2">
              <img
                class="my-content__empty-icon"
                :src="require('@/assets/images/icons/badge.svg')"
              />

              <p class="my-content__empty-text">
                When you join or complete something you'll see it here
              </p>
            </div>
          </template>
        </template>
        <template v-slot:myactivity>
          <collection
            v-if="hasWorkouts"
            class="pb-1 mb-1"
            card-display="horizontal"
            :items="workouts"
            :space-bottom-ratio="250"
            unlock
          >
            <h3 class="px-2 m-0">Recent Workouts</h3>
          </collection>

          <div v-else class="text-center mt-2 p-2">
            <img
              class="my-content__empty-icon"
              :src="require('@/assets/images/icons/badge.svg')"
            />

            <p class="my-content__empty-text">
              Your recent activities will show up here
            </p>
          </div>
        </template>
        <template v-slot:mysaved>
          <collection
            v-if="hasBookmarkedItems"
            class="pb-1 mb-1"
            card-display="horizontal"
            :items="bookmarkedItems"
            :show-price="false"
            :space-bottom-ratio="250"
            unlock
          >
            <h3 class="px-2 m-0">Saved</h3>
          </collection>

          <div v-else class="text-center mt-2 p-2">
            <img
              class="my-content__empty-icon"
              :src="require('@/assets/images/icons/badge.svg')"
            />

            <p class="my-content__empty-text">You'll see saved items here</p>
          </div>
        </template>
      </tabs>
    </div>

    <v-made-with-movement v-if="!isDesktop" />
  </v-page>
</template>

<script>
import { mapGetters, mapState, mapActions } from "vuex"

import { NAVIGATION_EVENTS } from "@/plugins/router/shared"
import { isPresent, formatSlug, pluralize } from "@/lib/utils"

import Toolbar from "@/components/Toolbar"
import Collection from "@/components/Collection"
import AddToHomescreen from "@/components/AddToHomescreen"
import Tabs from "@/components/Tabs"
import SubscriberAvatar from "@/components/SubscriberAvatar"

export default {
  components: {
    Toolbar,
    Collection,
    AddToHomescreen,
    Tabs,
    SubscriberAvatar,
    List: () => import("@/components/List"),
    ListItem: () => import("@/components/ListItem"),
    UnreadMarkerDot: () => import("@/components/message-thread/UnreadMarkerDot")
  },

  data() {
    return {
      isLoading: false
    }
  },

  computed: {
    ...mapState("myContent", [
      "workouts",
      "events",
      "programs",
      "packs",
      "pages",
      "sharedItems",
      "upcomingPages",
      "bookmarkedItems",
      "hasFormSubmissions",
      "purchasedProducts"
    ]),
    ...mapGetters("products", ["getAppProducts"]),
    ...mapState("responsive", ["isDesktop"]),
    ...mapState("messages", ["globalUnreadStatus"]),
    ...mapGetters(["subscription"]),
    ...mapGetters("auth", ["isAuthenticated", "currentUser"]),
    ...mapGetters("tabbar", ["tabbarLinks"]),
    ...mapGetters("pwa", ["canPrompt"]),

    hasEvents() {
      return isPresent(this.events)
    },

    hasSharedItems() {
      return isPresent(this.sharedItems)
    },

    hasPacks() {
      return isPresent(this.packs)
    },

    hasWorkouts() {
      return isPresent(this.workouts)
    },

    hasUpcomingPages() {
      return isPresent(this.upcomingPages)
    },

    hasAllPrograms() {
      return isPresent(this.allPrograms)
    },

    hasBookmarkedItems() {
      return isPresent(this.bookmarkedItems)
    },

    hasPurchases() {
      return isPresent(this.collectionPurchasedProducts)
    },

    hasMyContent() {
      return (
        this.hasPacks ||
        this.hasSharedItems ||
        this.hasUpcomingPages ||
        this.hasAllPrograms ||
        this.hasProgressByContentType ||
        this.hasEvents ||
        this.hasPurchases
      )
    },

    allPrograms() {
      return [this.progressByContentType["program"], this.programs]
        .flat()
        .filter(x => x)
    },

    collectionPurchasedProducts() {
      return this.purchasedProducts.reduce((acc, product) => {
        if (!this.getAppProducts.find(p => p.id == product.id)) {
          acc.push({
            type: "product",
            data: {
              ...product,
              path: `/me/products/${product.id}`
            }
          })
        }

        return acc
      }, [])
    },

    progressByContentType() {
      return this.pages.reduce((acc, page) => {
        if (!acc[page.content_type]) acc[page.content_type] = []

        acc[page.content_type].push(page)

        return acc
      }, {})
    },

    hasProgressByContentType() {
      return Object.values(this.progressByContentType).some(p => p.length > 0)
    },

    hasScheduleLink() {
      return !!this.tabbarLinks.find(link => link.path === "/me/schedule")
    },

    showMyChats() {
      return (
        this.subscription &&
        !this.subscription.is_free &&
        this.subscription.active
      )
    }
  },

  methods: {
    isPresent,
    formatSlug,
    pluralize,

    ...mapActions("myContent", ["fetchMyContent"]),
    ...mapActions("auth", ["logout"]),
    ...mapActions("messages", ["initializeGlobalUnreadChannel"]),

    collectionLayout(collection) {
      return collection.length > 1 ? "horizontal" : "vertical"
    },

    logoutUser() {
      this.logout().then(() => {
        this.$router.goHome()
      })
    },

    fetchContent() {
      if (this.isAuthenticated) {
        this.isLoading = true
        this.fetchMyContent().then(() => (this.isLoading = false))
      }
    }
  },

  mounted() {
    this.$router.on(NAVIGATION_EVENTS.POP, this.fetchContent)
  },

  beforeDestroy() {
    this.$router.off(NAVIGATION_EVENTS.POP, this.fetchContent)
  },

  watch: {
    isAuthenticated: {
      immediate: true,
      handler(authenticated) {
        if (authenticated) {
          this.fetchContent()
        }
      }
    }
  }
}
</script>

<style lang="scss">
@import "@/assets/styles/variables";

.my-content {
  .v-toolbar {
    .v-toolbar__content > div {
      height: 5rem;
    }
  }

  .v-list-item {
    .marker-dot {
      left: 0.75rem;
      top: 0.8rem;
    }
  }
}

.my-content__avatar {
  height: $size--9;
  width: $size--9;
  margin-right: $size--4;
  flex-shrink: 0;
}

.my-content__link-icon {
  // styles are similar to Download App button
  margin-right: 0.25rem;
  display: inline-flex;
  width: 53px;
  height: 53px;
  align-items: center;
  justify-content: center;
}

.my-content__empty-icon {
  max-width: 2rem;
}

.my-content__empty-text {
  max-width: 300px;
  margin-left: auto;
  margin-right: auto;
  font-weight: 700;
}

.my-content__tabs {
  background-color: transparent;

  .v-tabs__content-container {
    padding-top: 1.5rem;
  }
}
</style>
